// screens/AppendiceScreen.tsx — F1 / V28 S5: Appendice browser.
//   Level 1 (route /appendice)              → search hero + book-style argomenti index
//   Level 2 (route /appendice/:argomentoId) → principles list for that argomento
//
// V28 S5 restyle (docs/specs/v28-prototype/screens/appendice.html):
//   - The SEARCH bar is the visual hero (rounded pill, magnifier, helper line).
//   - The index is a book-style list of NUMBERED rows (leading 2-digit tabular
//     number from sort_order + name + chevron). No mastery colors here — the
//     Appendice is deliberately neutral, contrasting with Progressi.
//   - No in-screen back button: level-2 → level-1 is gesture-back (swipe-right /
//     left-edge tap via AppFrame) and the ☰ hamburger stays LEFT (TopChrome).
//
// Light-only throughout (tokens.mode always resolves 'light'); the search/index
// is read-only KB browse — no LLM, no persisted-identifier writes. The prototype
// shows a "N regole" per-row count + "331 regole / 25 argomenti" totals; the
// argomenti payload (ArgomentoDto) carries NO principle-count field, so those
// counts are NOT fabricated here — the index subtitle reports the real loaded
// argomento count and rows omit the (unavailable) per-argomento regola count.
import type { JSX } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import { kbApi } from '../api/kb';
import { appendiceApi, type AppendiceChapter } from '../api/appendice';
import { APPENDICE_HUES, argomentoHue, argomentoNumberFromId } from '../state/appendice-identity';
import { ProfProse } from '../components/ProfProse';
import type { ArgomentoDto, PrincipleDto } from '../api/kb';
// FP6 — bundled sign index (Segnali) + Search v2 ranked sign results.
import { searchSigns, type AppendiceSearchResult } from '../utils/appendice-search';

// ---------------------------------------------------------------------------
// Utility: accent-insensitive Italian match + 2-digit book number
// ---------------------------------------------------------------------------

function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Book-style 2-digit row number, e.g. 1 → "01", 14 → "14". */
function bookNumber(n: number | undefined | null): string {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  return String(Math.max(0, Math.trunc(v))).padStart(2, '0');
}

function canonicalArgomentoNumber(arg: ArgomentoDto, fullListIndex: number): number {
  const idMatch = /^arg(\d+)$/i.exec(arg.id);
  if (idMatch) {
    const parsed = Number(idMatch[1]);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fullListIndex + 1;
}

// ---------------------------------------------------------------------------
// ArgomentoCard — V28 book-style numbered row (number + name + chevron).
// ---------------------------------------------------------------------------

interface ArgomentoCardProps {
  arg: ArgomentoDto;
  displayNumber: number;
  onClick: () => void;
}

function ArgomentoCard({ arg, displayNumber, onClick }: ArgomentoCardProps): JSX.Element {
  const tokens = useTokens();
  const reduced = useReducedMotion();
  // Restyle (2026-07-14): each argomento's identity hue colors its medallion
  // + left rail — color as wayfinding, stable across filtering.
  const hue = APPENDICE_HUES[(Math.max(1, displayNumber) - 1) % APPENDICE_HUES.length];
  return (
    <button
      onClick={onClick}
      style={{
        position:     'relative',
        overflow:     'hidden',
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: 14,
        minHeight:    44,
        padding:      '10px 14px 10px 16px',
        cursor:       'pointer',
        textAlign:    'left',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        width:        '100%',
        transition:   reduced ? 'none' : 'background 0.16s ease',
      }}
    >
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: hue.solid }} />
      {/* Leading canonical 2-digit book number as a hue medallion. */}
      <span
        style={{
          flexShrink:           0,
          width:                38,
          height:               38,
          borderRadius:         12,
          display:              'flex',
          alignItems:           'center',
          justifyContent:       'center',
          background:           hue.tint,
          color:                hue.text,
          fontFamily:           tokens.fonts.body,
          fontSize:             13,
          fontWeight:           800,
          fontVariantNumeric:   'tabular-nums',
          fontFeatureSettings:  '"tnum" 1',
        }}
      >
        {bookNumber(displayNumber)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily:   tokens.fonts.body,
            fontSize:     14,
            fontWeight:   700,
            color:        tokens.text,
            letterSpacing: -0.1,
            lineHeight:   1.3,
            // V29 S4 (audit A8): argomento names wrap to two lines instead of
            // truncating mid-word ("…defini…"); clamp keeps rows bounded.
            display:            '-webkit-box',
            WebkitLineClamp:    2,
            WebkitBoxOrient:    'vertical',
            overflow:           'hidden',
          }}
        >
          {arg.name_it}
        </div>
      </div>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={tokens.textFaint}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// PrincipleRow — used in level-2 list (number + name + chevron).
// ---------------------------------------------------------------------------

interface PrincipleRowProps {
  principle: PrincipleDto;
  /** The parent argomento's identity hue (restyle 2026-07-14). */
  hue: ReturnType<typeof argomentoHue>;
  onClick: () => void;
}

function PrincipleRow({ principle, hue, onClick }: PrincipleRowProps): JSX.Element {
  const tokens = useTokens();
  const reduced = useReducedMotion();
  return (
    <button
      onClick={onClick}
      style={{
        position:     'relative',
        overflow:     'hidden',
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: 14,
        minHeight:    44,
        padding:      '10px 14px 10px 16px',
        cursor:       'pointer',
        textAlign:    'left',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        width:        '100%',
        transition:   reduced ? 'none' : 'background 0.16s ease',
      }}
    >
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: hue.solid }} />
      <span
        style={{
          flexShrink:          0,
          width:               34,
          height:              34,
          borderRadius:        11,
          display:             'flex',
          alignItems:          'center',
          justifyContent:      'center',
          background:          hue.tint,
          color:               hue.text,
          fontFamily:          tokens.fonts.body,
          fontSize:            12.5,
          fontWeight:          800,
          fontVariantNumeric:  'tabular-nums',
          fontFeatureSettings: '"tnum" 1',
        }}
      >
        {principle.principle_number}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily:   tokens.fonts.body,
            fontSize:     13.5,
            fontWeight:   600,
            color:        tokens.text,
            letterSpacing: -0.1,
            whiteSpace:   'nowrap',
            overflow:     'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {principle.name_it}
        </div>
      </div>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={tokens.textFaint}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SegnaliEntry — FP6: the L1 entry into the Segnali sign index (SignIndexScreen).
// Accent-tinted card above the book-style argomenti index. A SUB-section of the
// Appendice, NOT a top-level drawer item (the 5 student sections are preserved).
// ---------------------------------------------------------------------------

function SegnaliEntry({ onClick }: { onClick: () => void }): JSX.Element {
  const tokens = useTokens();
  return (
    <button
      onClick={onClick}
      data-testid="appendice-segnali-entry"
      style={{
        /* Restyle (2026-07-14): the sign index reads as a dark "sign panel" —
           visually its own world, distinct from the hue-coded argomenti. */
        background: 'linear-gradient(120deg, #1a1535, #3c2f66)', border: 'none', borderRadius: tokens.radius.lg,
        padding: '14px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 19, letterSpacing: 2, flexShrink: 0 }}>🔺🚫🔵</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 14.5, fontWeight: 700, color: '#fff', letterSpacing: -0.1 }}>
          Segnali
        </div>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 12.5, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
          Indice dei segnali: nome ufficiale, figura, forma e trappole
        </div>
      </div>
      <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M3 6h6M7 3l3 3-3 3" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// DomandeEntryRow — FP6: leads into the official-question browser for the
// argomento (QuestionBrowser, route /appendice/:argomentoId/domande). Mirrors
// an accent-tinted entry row — a SEPARATE route, not a tab.
// ---------------------------------------------------------------------------

function DomandeEntryRow({
  tokens,
  onClick,
}: {
  tokens: ReturnType<typeof useTokens>;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      data-testid="domande-entry"
      style={{
        /* Restyle (2026-07-14): the questions entry is the page's one filled
           gradient CTA. */
        background:   'linear-gradient(120deg, #6943ff, #8f4bee)',
        border:       'none',
        borderRadius: 14,
        minHeight:    44,
        padding:      '12px 14px',
        cursor:       'pointer',
        textAlign:    'left',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        width:        '100%',
        marginBottom: 4,
      }}
    >
      <svg
        width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: -0.1 }}>
          Domande d&rsquo;esame
        </div>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted, marginTop: 1 }}>
          Le domande ufficiali con la spiegazione
        </div>
      </div>
      <svg
        width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="rgba(255,255,255,0.8)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// AppendiceScreen
// ---------------------------------------------------------------------------

export default function AppendiceScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { argomentoId } = useParams<{ argomentoId?: string }>();

  const [search, setSearch] = useState('');

  // Level 1 state
  const [argomenti, setArgomenti] = useState<ArgomentoDto[]>([]);
  const [argomentiLoading, setArgomentiLoading] = useState(false);
  const [argomentiError, setArgomentiError] = useState<string | null>(null);

  // Level 2 state
  const [principles, setPrinciples] = useState<PrincipleDto[]>([]);
  // Appendice restructure (2026-07-14): the argomento page is the chapter HUB —
  // it frames the sotto-argomenti with the chapter's "In breve" intro on top
  // and the "Ripasso rapido" recap at the bottom (both used to live only inside
  // the removed "Teoria dell'argomento" page). Best-effort: null hides both.
  const [chapter, setChapter] = useState<AppendiceChapter | null>(null);
  // Restyle (2026-07-14): the hue header shows real counts — sotto-argomenti
  // from the loaded principles, domande from the cached questions list.
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [principlesLoading, setPrinciplesLoading] = useState(false);
  const [principlesError, setPrinciplesError] = useState<string | null>(null);
  const [argomentoName, setArgomentoName] = useState<string>('');

  const isLevel2 = !!argomentoId;
  const hue = argomentoHue(argomentoId);

  // ---------------------------------------------------------------------------
  // Level 1: load argomenti
  // ---------------------------------------------------------------------------
  const loadArgomenti = useCallback(async () => {
    setArgomentiLoading(true);
    setArgomentiError(null);
    try {
      const data = await kbApi.listArgomenti();
      setArgomenti(data);
    } catch {
      setArgomentiError('Non sono riuscito a caricare gli argomenti. Controlla la connessione e riprova.');
    } finally {
      setArgomentiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLevel2) loadArgomenti();
  }, [isLevel2, loadArgomenti]);

  // ---------------------------------------------------------------------------
  // Level 2: load principles for argomento
  // ---------------------------------------------------------------------------
  const loadPrinciples = useCallback(async (id: string) => {
    setPrinciplesLoading(true);
    setPrinciplesError(null);
    try {
      const data = await kbApi.listPrinciples(id);
      setPrinciples(data);
      // Derive argomento name from first principle or cached argomenti list
      const name = data[0]?.argomenti?.name_it ?? argomenti.find((a) => a.id === id)?.name_it ?? '';
      setArgomentoName(name);
    } catch {
      setPrinciplesError('Non sono riuscito a caricare i principi di questo argomento. Riprova tra qualche secondo.');
    } finally {
      setPrinciplesLoading(false);
    }
  }, [argomenti]);

  useEffect(() => {
    if (isLevel2 && argomentoId) loadPrinciples(argomentoId);
  }, [isLevel2, argomentoId, loadPrinciples]);

  useEffect(() => {
    if (!isLevel2 || !argomentoId) return;
    let cancelled = false;
    setChapter(null);
    appendiceApi
      .getChapterCached(argomentoId)
      .then((data) => { if (!cancelled) setChapter(data); })
      .catch(() => { if (!cancelled) setChapter(null); });
    return () => { cancelled = true; };
  }, [isLevel2, argomentoId]);

  useEffect(() => {
    if (!isLevel2 || !argomentoId) return;
    let cancelled = false;
    setQuestionCount(null);
    setIntroExpanded(false);
    kbApi.listArgomentoQuestions(argomentoId)
      .then((qs) => { if (!cancelled) setQuestionCount(qs.length); })
      .catch(() => { if (!cancelled) setQuestionCount(null); });
    return () => { cancelled = true; };
  }, [isLevel2, argomentoId]);

  // ---------------------------------------------------------------------------
  // Filtered argomenti (level 1 search)
  // ---------------------------------------------------------------------------
  const filteredArgomenti = search
    ? argomenti.filter((a) => normalise(a.name_it).includes(normalise(search)))
    : argomenti;
  const argomentoNumbers = useMemo(
    () => new Map(argomenti.map((arg, index) => [arg.id, canonicalArgomentoNumber(arg, index)])),
    [argomenti],
  );

  // ---------------------------------------------------------------------------
  // Level 1 content search — when the student types a query that doesn't
  // match any argomento name, we ALSO search inside principle content
  // (name_it + regola + enunciato) via the backend. Debounced so fast typing
  // doesn't fire one request per keystroke.
  // ---------------------------------------------------------------------------
  const [principleResults, setPrincipleResults] = useState<PrincipleDto[]>([]);
  const [principleSearchLoading, setPrincipleSearchLoading] = useState(false);

  // FP6 Search v2 — local ranked SIGN results from the bundled sign index. One
  // unified L1 search: signs (local, synchronous) render ABOVE the debounced
  // principle-content matches below; both share the same hero query.
  const signResults: AppendiceSearchResult[] = useMemo(
    () => (search.trim().length >= 2 ? searchSigns(search, 8) : []),
    [search],
  );

  useEffect(() => {
    if (isLevel2) return;
    const term = search.trim();
    if (term.length < 2) {
      setPrincipleResults([]);
      setPrincipleSearchLoading(false);
      return;
    }
    setPrincipleSearchLoading(true);
    const id = window.setTimeout(() => {
      kbApi.searchPrinciples(term)
        .then((res) => setPrincipleResults(res))
        .catch(() => setPrincipleResults([]))
        .finally(() => setPrincipleSearchLoading(false));
    }, 250);
    return () => window.clearTimeout(id);
  }, [search, isLevel2]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  // Search HERO — the prominent rounded-pill field that anchors the index
  // (prototype appendice.html L22-32). Magnifier + input + a centered faint
  // helper line below it.
  const searchHero = (
    <div style={{ padding: '14px 16px 0' }}>
      {/* Restyle (2026-07-14): the gradient hero band owns the section's
          identity; the search pill lives inside it. Real counts only. */}
      <div
        data-testid="appendice-hero-band"
        style={{
          background:   tokens.brandGradient,
          borderRadius: 16,
          padding:      '14px 16px 16px',
          color:        '#fff',
        }}
      >
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 15, fontWeight: 800, letterSpacing: -0.2 }}>
          Il manuale, sempre con te
        </div>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 11.5, opacity: 0.9, marginTop: 2, marginBottom: 10 }}>
          {argomenti.length > 0 ? `${argomenti.length} argomenti · ` : ''}teoria, trappole e domande ufficiali
        </div>
      <div
        style={{
          display:      'flex',
          alignItems:   'center',
          gap:          8,
          minHeight:    48,
          background:   'rgba(255,255,255,0.96)',
          border:       'none',
          borderRadius: tokens.radius.pill,
          padding:      '0 16px',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={tokens.textFaint}
          strokeWidth="2.2"
          strokeLinecap="round"
          style={{ flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="search"
          placeholder="Cerca una regola, un segnale…"
          aria-label="Cerca nell'Appendice"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex:       1,
            minHeight:  44,
            background: 'transparent',
            border:     'none',
            outline:    'none',
            fontFamily: tokens.fonts.body,
            fontSize:   15,
            color:      tokens.text,
          }}
        />
      </div>
      <p
        style={{
          margin:     '8px 0 0',
          fontFamily: tokens.fonts.body,
          fontSize:   12,
          color:      'rgba(255,255,255,0.85)',
          textAlign:  'center',
        }}
      >
        Cerca tra tutte le regole &middot; segnali, precedenze, norme
      </p>
      </div>
    </div>
  );

  return (
    <Screen>
      <TopChrome
        title={isLevel2 ? (argomentoName || 'Principi') : 'Appendice'}
        subtitle={
          isLevel2
            ? (principles.length > 0 ? `${principles.length} regole` : 'Principi')
            : 'Tutto il Codice della Strada'
        }
        // Hierarchical back (founder 2026-07-14 r4): the arrow always goes ONE
        // level up — deterministic navigate, never history (history can point
        // DOWN after an explicit up-navigation pushed an entry).
        onBack={isLevel2 ? () => navigate('/appendice', { replace: true }) : undefined}
      />

      {/* Level 1: search hero + book-style argomenti index */}
      {!isLevel2 && (
        <>
          {searchHero}
          <main
            className="asc-scroll"
            style={{
              flex:      1,
              overflow:  'auto',
              padding:   '12px 16px 24px',
              display:   'flex',
              flexDirection: 'column',
              gap:       8,
            }}
          >
            {argomentiLoading && (
              /* V29 S4 (audit A6): skeleton rows instead of bare "Caricamento…". */
              <div data-testid="appendice-index-skeleton" aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ height: 56, borderRadius: 12, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
                ))}
              </div>
            )}
            {argomentiError && (
              <div
                style={{
                  fontFamily: tokens.fonts.body,
                  fontSize:   13,
                  color:      tokens.wrong,
                  padding:    '12px 0',
                }}
              >
                {argomentiError}
              </div>
            )}

            {/* FP6 Segnali entry — the sign index, shown above the book-style
                argomenti index when not actively searching. A sub-section of the
                Appendice (not a drawer item). */}
            {!argomentiLoading && !argomentiError && search.trim().length < 2 && (
              <SegnaliEntry onClick={() => navigate('/appendice/segnali')} />
            )}

            {/* FP6 Search v2 — local ranked SIGN results, surfaced ABOVE the
                principle-content matches for queries ≥ 2 chars. */}
            {search.trim().length >= 2 && signResults.length > 0 && (
              <section>
                <div
                  style={{
                    fontFamily:    tokens.fonts.mono,
                    fontSize:      11,
                    fontWeight:    700,
                    color:         tokens.textMuted,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    margin:        '4px 0 8px',
                  }}
                >
                  Segnali
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {signResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => navigate(`/appendice/segnali/${r.id}`)}
                      style={{
                        display:      'block',
                        width:        '100%',
                        textAlign:    'left',
                        background:   tokens.surface,
                        border:       `1px solid ${tokens.line}`,
                        borderRadius: tokens.radius.md,
                        padding:      '10px 12px',
                        cursor:       'pointer',
                        fontFamily:   tokens.fonts.body,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: tokens.text, marginBottom: 2 }}>
                        {r.title}
                      </div>
                      {r.subtitle && (
                        <div style={{ fontSize: 12, color: tokens.textMuted }}>
                          {r.subtitle}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Book-style section label above the index stack. Count is the real
                loaded argomento count — no fabricated total. */}
            {!argomentiLoading && !argomentiError && filteredArgomenti.length > 0 && (
              <div
                style={{
                  fontFamily:    tokens.fonts.mono,
                  fontSize:      11,
                  fontWeight:    700,
                  color:         tokens.textMuted,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  margin:        '4px 2px 4px',
                }}
              >
                Indice &middot; {filteredArgomenti.length} argomenti
              </div>
            )}

            {!argomentiLoading &&
              filteredArgomenti.map((arg, i) => (
                <ArgomentoCard
                  key={arg.id}
                  arg={arg}
                  displayNumber={argomentoNumbers.get(arg.id) ?? i + 1}
                  onClick={() => navigate(`/appendice/${arg.id}`)}
                />
              ))}
            {!argomentiLoading && !argomentiError && filteredArgomenti.length === 0 && principleResults.length === 0 && signResults.length === 0 && !principleSearchLoading && search.trim().length >= 2 && (
              <div
                style={{
                  fontFamily: tokens.fonts.body,
                  fontSize:   14,
                  color:      tokens.textMuted,
                  padding:    '24px 0',
                  textAlign:  'center',
                }}
              >
                Nessun risultato per &ldquo;{search}&rdquo;.
              </div>
            )}

            {/* Principle-content matches — only surface when the user is
                searching (≥2 chars) and we have hits that aren't already
                obviously in filteredArgomenti above. */}
            {search.trim().length >= 2 && (principleSearchLoading || principleResults.length > 0) && (
              <section style={{ marginTop: filteredArgomenti.length > 0 ? 16 : 0 }}>
                <div
                  style={{
                    fontFamily:    tokens.fonts.mono,
                    fontSize:      11,
                    fontWeight:    700,
                    color:         tokens.textMuted,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    margin:        '12px 0 8px',
                  }}
                >
                  Principi che contengono &ldquo;{search}&rdquo;
                </div>
                {principleSearchLoading && (
                  <div style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted, padding: '6px 0' }}>
                    Cercando…
                  </div>
                )}
                {!principleSearchLoading && principleResults.map((p) => {
                  const argName = Array.isArray(p.argomenti) ? (p.argomenti[0] as { name_it?: string })?.name_it : p.argomenti?.name_it;
                  return (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/appendice/${p.argomento_id}/${p.id}`)}
                      style={{
                        display:      'block',
                        width:        '100%',
                        textAlign:    'left',
                        background:   tokens.surface,
                        border:       `1px solid ${tokens.line}`,
                        borderRadius: tokens.radius.md,
                        padding:      '10px 12px',
                        marginBottom: 6,
                        cursor:       'pointer',
                        fontFamily:   tokens.fonts.body,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: tokens.text, marginBottom: 2 }}>
                        {p.name_it}
                      </div>
                      <div style={{ fontSize: 12, color: tokens.textMuted }}>
                        {argName ?? p.argomento_id}
                      </div>
                    </button>
                  );
                })}
              </section>
            )}
          </main>
        </>
      )}

      {/* Level 2: principles list. Back to the index is gesture-only (swipe-right
          / left-edge tap via AppFrame) + the ☰ hamburger (left) — no in-screen
          back button (V28 gesture-nav contract). */}
      {isLevel2 && (
        <main
          className="asc-scroll"
          style={{
            flex:      1,
            overflow:  'auto',
            padding:   '16px 16px 24px',
            display:   'flex',
            flexDirection: 'column',
            gap:       6,
          }}
        >
          {principlesLoading && (
            <div
              style={{
                fontFamily: tokens.fonts.body,
                fontSize:   14,
                color:      tokens.textMuted,
                padding:    '24px 0',
                textAlign:  'center',
              }}
            >
              Caricamento principi…
            </div>
          )}
          {principlesError && (
            <div
              style={{
                fontFamily: tokens.fonts.body,
                fontSize:   13,
                color:      tokens.wrong,
                padding:    '12px 0',
              }}
            >
              {principlesError}
            </div>
          )}
          {/* Restyle (2026-07-14): the argomento's identity hue owns the page —
              header band with the watermark number + real counts. */}
          {!principlesLoading && !principlesError && argomentoId && (
            <div
              data-testid="argomento-hue-header"
              style={{
                position:     'relative',
                overflow:     'hidden',
                flexShrink:   0,
                borderRadius: 16,
                padding:      '16px 16px 14px',
                marginBottom: 8,
                color:        '#fff',
                background:   hue.grad,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', right: -6, top: -20,
                  fontFamily: tokens.fonts.body, fontSize: 92, fontWeight: 800,
                  opacity: 0.16, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                }}
              >
                {String(argomentoNumberFromId(argomentoId)).padStart(2, '0')}
              </span>
              <div style={{ position: 'relative', fontFamily: tokens.fonts.body, fontSize: 16, fontWeight: 800, lineHeight: 1.25, paddingRight: 56 }}>
                {argomentoName || 'Argomento'}
              </div>
              <div style={{ position: 'relative', fontFamily: tokens.fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', opacity: 0.88, marginTop: 4 }}>
                {principles.length} sotto-argoment{principles.length === 1 ? 'o' : 'i'}
                {questionCount != null ? ` · ${questionCount} domande ufficiali` : ''}
              </div>
            </div>
          )}
          {/* Chapter hub (2026-07-14): the "In breve" intro from the manual
              chapter opens the page — the separate "Teoria dell'argomento"
              entry is gone (the theory lives in the sotto-argomenti). The
              small "Leggi tutto di fila" link keeps the linear chapter reader
              for start-to-finish study (founder: keep it). */}
          {!principlesLoading && !principlesError && chapter?.capitolo_intro_md && (
            <div
              data-testid="argomento-in-breve"
              style={{
                background:   hue.soft,
                border:       `1px solid ${hue.border}`,
                borderRadius: tokens.radius.md,
                padding:      '13px 15px',
                marginBottom: 4,
                flexShrink:   0,
              }}
            >
              <div
                style={{
                  fontFamily:    tokens.fonts.mono,
                  fontSize:      10.5,
                  fontWeight:    700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color:         hue.text,
                  marginBottom:  6,
                }}
              >
                In breve
              </div>
              {/* Real chapter intros run long — clamp so the sotto-argomenti
                  stay near the fold; one tap expands. */}
              <div
                style={introExpanded ? undefined : {
                  display:         '-webkit-box',
                  WebkitLineClamp: 6,
                  WebkitBoxOrient: 'vertical',
                  overflow:        'hidden',
                }}
              >
                <ProfProse text={chapter.capitolo_intro_md} serif={false} size={13.5} />
              </div>
              <button
                type="button"
                data-testid="in-breve-toggle"
                onClick={() => setIntroExpanded((v) => !v)}
                style={{
                  marginTop:  6,
                  background: 'transparent',
                  border:     'none',
                  padding:    '2px 0',
                  cursor:     'pointer',
                  fontFamily: tokens.fonts.body,
                  fontSize:   12,
                  fontWeight: 700,
                  color:      hue.text,
                }}
              >
                {introExpanded ? 'Mostra meno' : 'Continua a leggere ›'}
              </button>
            </div>
          )}
          {!principlesLoading && !principlesError && argomentoId && chapter && (
            <button
              type="button"
              data-testid="leggi-tutto-link"
              onClick={() => navigate(`/appendice/${argomentoId}/teoria`)}
              style={{
                alignSelf:  'flex-start',
                background: 'transparent',
                border:     'none',
                padding:    '2px 2px 8px',
                cursor:     'pointer',
                fontFamily: tokens.fonts.body,
                fontSize:   12.5,
                fontWeight: 700,
                color:      hue.text,
              }}
            >
              Leggi tutto di fila &rsaquo;
            </button>
          )}
          {/* FP6 — official-question browser entry (separate route, not a tab). */}
          {!principlesLoading && !principlesError && argomentoId && (
            <DomandeEntryRow
              tokens={tokens}
              onClick={() => navigate(`/appendice/${argomentoId}/domande`)}
            />
          )}
          {!principlesLoading &&
            principles.map((p) => (
              <PrincipleRow
                key={p.id}
                principle={p}
                hue={hue}
                onClick={() => navigate(`/appendice/${argomentoId}/${p.id}`)}
              />
            ))}
          {!principlesLoading && !principlesError && chapter?.capitolo_recap_md && (
            <div
              data-testid="argomento-ripasso"
              style={{
                background:   tokens.surface,
                border:       `1px solid ${tokens.line}`,
                borderRadius: tokens.radius.md,
                padding:      '13px 15px',
                marginTop:    8,
              }}
            >
              <div
                style={{
                  fontFamily:    tokens.fonts.mono,
                  fontSize:      10.5,
                  fontWeight:    700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color:         hue.text,
                  marginBottom:  6,
                }}
              >
                Ripasso rapido
              </div>
              <ProfProse text={chapter.capitolo_recap_md} serif={false} size={13} />
            </div>
          )}
          {!principlesLoading && !principlesError && principles.length === 0 && (
            <div
              style={{
                fontFamily: tokens.fonts.body,
                fontSize:   14,
                color:      tokens.textMuted,
                padding:    '24px 0',
                textAlign:  'center',
              }}
            >
              Nessun principio trovato.
            </div>
          )}
        </main>
      )}
    </Screen>
  );
}
